import { useEffect, useRef, useState } from 'react';
import { track } from '../../services/analytics';
import type { Copy } from './copy';

/*
 * Cooking Mode you can click: Back and Next step through Shakshuka the way the app
 * does, the segments fill, and each step slides in from the side you're heading. While
 * on screen it also steps on its own; a click hands control to the visitor for a while.
 * Reduced motion keeps the buttons and drops the autoplay and the slide.
 */

const AUTO_MS = 3800;
/** After a click, autoplay waits this long before taking over again. */
const HANDS_OFF_MS = 12000;

const Svg = ({ d, className = 'i' }: { d: string; className?: string }) => (
  <svg className={className} viewBox="0 0 24 24" aria-hidden="true">
    <path d={d} />
  </svg>
);
const TIMER = 'M12 13.5V10M10 3h4M19.5 13.5a7.5 7.5 0 11-15 0 7.5 7.5 0 0115 0z';

/** "8 min" → 8, for the running timer the pill shows on the step after. */
const minutesOf = (label: string) => Number.parseInt(label, 10) || 0;

export const CookDemo = ({ c }: { c: Copy }) => {
  const k = c.cook;
  const total = k.steps.length;
  const ref = useRef<HTMLDivElement>(null);
  const [still] = useState(() => window.matchMedia('(prefers-reduced-motion: reduce)').matches);
  const [step, setStep] = useState(0);
  const [dir, setDir] = useState<'next' | 'back'>('next');
  const [visible, setVisible] = useState(false);
  const [handsOffUntil, setHandsOffUntil] = useState(0);

  useEffect(() => {
    const el = ref.current;
    if (!el || still) return;
    const io = new IntersectionObserver(([entry]) => setVisible(entry.isIntersecting), {
      threshold: 0.4,
    });
    io.observe(el);
    return () => io.disconnect();
  }, [still]);

  useEffect(() => {
    if (still || !visible) return;
    const wait = Math.max(AUTO_MS, handsOffUntil - Date.now());
    const timer = window.setTimeout(() => {
      setDir('next');
      setStep((s) => (s + 1) % total);
    }, wait);
    return () => window.clearTimeout(timer);
  }, [step, visible, still, handsOffUntil, total]);

  const go = (to: number, direction: 'next' | 'back', restart = false) => {
    track('landing_cook_demo_stepped', {
      direction: restart ? 'restart' : direction,
      step: to + 1,
    });
    setDir(direction);
    setStep(to);
    setHandsOffUntil(Date.now() + HANDS_OFF_MS);
  };
  const last = step === total - 1;
  const current = k.steps[step];
  const [before, minutes, after] = current.text;
  const previous = step > 0 ? k.steps[step - 1] : null;
  const runningMinutes = previous ? minutesOf(previous.text[1]) : 0;

  return (
    <div className="phone cook-demo" ref={ref} role="group" aria-label={k.phoneLabel}>
      <div className="screen">
        <div className="app" style={{ zoom: 300 / 390 }}>
          <div className="status" aria-hidden="true">
            <span>9:41</span>
            <span className="bat">
              <i />
            </span>
          </div>
          <div className="ck-top" aria-hidden="true">
            <span className="disc">
              <Svg d="M6 6l12 12M18 6L6 18" />
            </span>
            <span className="ck-count">{k.counter(step + 1, total)}</span>
            <span className="disc">
              <Svg d={TIMER} />
              {runningMinutes > 0 && <span className="bdg">1</span>}
            </span>
          </div>
          <div className="segs" aria-hidden="true">
            {k.steps.map((_, n) => (
              <i key={n} className={n <= step ? 'f' : undefined} />
            ))}
          </div>
          <div key={step} className={`ck-body${still ? '' : ` slide-${dir}`}`} aria-live="polite">
            <div className="label">{k.stepLabel(step + 1)}</div>
            <p className="instr">
              {before}
              {minutes && (
                <>
                  {' '}
                  <span className="inl">
                    <Svg d={TIMER} />
                    {minutes}
                  </span>
                </>
              )}
              {after}
            </p>
            <div className="label reach">{k.reach}</div>
            <div className="ingchips">
              {current.reach.map(([qty, name]) => (
                <span key={name}>
                  {qty && <b>{qty}</b>} {name}
                </span>
              ))}
            </div>
          </div>
          <div className="ck-foot">
            <div className={`pill${runningMinutes > 0 ? '' : ' off'}`} aria-hidden="true">
              <Svg d={TIMER} />
              {Math.max(runningMinutes - 1, 0)}:42<span>{k.timerStep(step)}</span>
              <Svg d="M9 6v12M15 6v12" />
            </div>
            <div className="ck-nav">
              <button
                type="button"
                className="btn-app sec2"
                disabled={step === 0}
                onClick={() => go(step - 1, 'back')}
              >
                {k.back}
              </button>
              <button
                type="button"
                className="btn-app"
                aria-label={last ? k.startOver : undefined}
                onClick={() => go(last ? 0 : step + 1, last ? 'back' : 'next', last)}
              >
                {last ? k.finish : k.next}
                {!last && <Svg d="M5 12h14M13 6l6 6-6 6" />}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
